import styled from "@emotion/styled";
import { css } from "@styled-system/css";
import shouldForwardProp from "@styled-system/should-forward-prop";
import { useRouter } from "next/router";
import React, { useCallback, useState } from "react";
import { compose, margin, MarginProps } from "styled-system";

import { Box, Button, FieldError, TextField } from "~/components/blocks";

type Props = {
  defaultValue?: string;
  onClick: (value: string) => void;
  disabled: boolean;
} & MarginProps;

const Root = styled(Box)(() =>
  css({
    position: "relative",
  }),
);

const Error = styled(Box)(({ theme }) =>
  css({
    marginTop: theme.space.xs,
    paddingLeft: theme.space.s,
    position: "absolute",
  }),
);

const Search = styled("div", {
  shouldForwardProp,
})<MarginProps>(
  () =>
    css({
      display: "flex",
      [`& > ${TextField}`]: {
        background: "#223344",
      },
    }),
  compose(margin),
);

const SearchButton = styled(Button)(({ theme }) =>
  css({
    borderRadius: `0 ${theme.radii.default} ${theme.radii.default} 0`,
    "&:hover": css({
      opacity: "0.8",
    }),
  }),
);

const SearchField = styled(TextField)(({ theme }) =>
  css({
    fontSize: theme.fontSizes.l,
    background: theme.colors.background.default,
    borderRadius: `${theme.radii.default} 0 0 ${theme.radii.default}`,
    border: theme.borders.primary,
    "&:hover": {
      boxShadow: theme.shadows.green,
    },
    "&:focus": {
      boxShadow: theme.shadows.default,
    },
  }),
);

type Query = {
  name?: string;
};

export const InputSearch = React.memo((props: Props) => {
  const { defaultValue, onClick, disabled, ...styles } = props;

  const router = useRouter();
  const query = router.query as Query;
  const name = query.name || "";

  const [value, setValue] = useState(defaultValue ?? "");
  const [error, setError] = useState<string | null>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setError(null);
      setValue(event.currentTarget.value);
    },
    [],
  );
  const handleClick = useCallback(() => {
    if (name === value) {
      return;
    }

    if (value.length > 0) {
      onClick(value);
    } else {
      setError("薬局名を入力してください");
    }
  }, [name, onClick, value]);

  return (
    <Root>
      <Search {...styles}>
        <SearchField
          placeholder="薬局名で検索"
          value={value}
          onChange={handleChange}
          disabled={disabled}
        />
        <SearchButton
          type="submit"
          use="base"
          onClick={handleClick}
          disabled={disabled}
        >
          検索
        </SearchButton>
      </Search>
      {error && (
        <Error>
          <FieldError color="white" fontSize="xs" error={error} />
        </Error>
      )}
    </Root>
  );
});

InputSearch.displayName = "InputSearch";
